import { useEffect } from 'react'
import { useTimerStore } from '@/shared/stores/timerStore'
import { timerService } from '@/shared/services/timerService'
import { timerPersistence } from '@/shared/utils/timerPersistence'

export function useTimerSync(): void {
  const restoreFromStorage = useTimerStore((state) => state.restoreFromStorage)
  
  useEffect(() => {
    const syncFromServer = async () => {
      try {
        const activeTimer = await timerService.getActiveTimer()
        if (activeTimer) {
          restoreFromStorage({
            status: activeTimer.isRunning ? 'running' : 'paused',
            projectId: activeTimer.projectId ?? null,
            taskId: activeTimer.taskId ?? null,
            description: activeTimer.description ?? '',
            tagIds: [],
            startedAt: activeTimer.startedAt ?? null,
            pausedAt: null,
            elapsedSeconds: activeTimer.elapsedSeconds ?? 0,
            isMinimized: false,
          })
          return // Server state takes precedence
        }
      } catch {
        // Server unreachable or no workspace — fall through to local storage
      }

      // Load timer state from local storage
      const persisted = timerPersistence.load()
      if (persisted) {
        restoreFromStorage(persisted)
      }
    }

    syncFromServer()
    
    // Subscribe to cross-tab storage changes
    const unsubscribe = timerPersistence.subscribe((state) => {
      if (state) {
        restoreFromStorage(state)
      }
    })
    
    return unsubscribe
  }, [restoreFromStorage])
  
  // Save to storage whenever timer state changes
  useEffect(() => {
    const unsubscribe = useTimerStore.subscribe((state) => {
      timerPersistence.save({
        status: state.status,
        projectId: state.projectId,
        taskId: state.taskId,
        description: state.description,
        tagIds: state.tagIds,
        elapsedSeconds: state.elapsedSeconds,
        startedAt: state.startedAt,
        pausedAt: state.pausedAt,
        isMinimized: state.isMinimized,
      })
    })
    
    return unsubscribe
  }, [])
}

export default useTimerSync
